import React, { useState, useEffect } from 'react';

const NPSCalculator: React.FC = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(4000);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [percentAnnuity, setPercentAnnuity] = useState(40);
  const [annuityReturn, setAnnuityReturn] = useState(1);
  const [annuityPeriod, setAnnuityPeriod] = useState(10);

  const [pensionWealth, setPensionWealth] = useState(0);
  const [investedAmount, setInvestedAmount] = useState(0);
  const [interestEarned, setInterestEarned] = useState(0);
  const [taxSaving, setTaxSaving] = useState(0);
  const [pensionPerMonth, setPensionPerMonth] = useState(0);
  const [reinvestedAmount, setReinvestedAmount] = useState(0);
  const [lumpsumWithdrawn, setLumpsumWithdrawn] = useState(0);

  useEffect(() => {
    calculateResults();
  }, [monthlyInvestment, expectedReturn, currentAge, retirementAge, percentAnnuity, annuityReturn, annuityPeriod]);

  const calculateResults = () => {
    const investingYears = retirementAge - currentAge;
    const n = investingYears * 12; // total number of months
    const r = expectedReturn / 12 / 100; // monthly rate

    // Future value of SIP formula
    const pensionWealthGenerated = monthlyInvestment * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);

    const totalInvested = monthlyInvestment * n;
    const interest = pensionWealthGenerated - totalInvested;
    const taxSaved = totalInvested * 0.3; // assuming 30% tax bracket

    const annuityAmount = pensionWealthGenerated * (percentAnnuity / 100);
    const lumpsumAmount = pensionWealthGenerated - annuityAmount;

    const annuityRateMonthly = annuityReturn / 12 / 100;
    const annuityMonths = annuityPeriod * 12;

    const pensionMonthly = (annuityAmount * annuityRateMonthly) / (1 - Math.pow(1 + annuityRateMonthly, -annuityMonths));

    // Set all values
    setPensionWealth(pensionWealthGenerated);
    setInvestedAmount(totalInvested);
    setInterestEarned(interest);
    setTaxSaving(taxSaved);
    setReinvestedAmount(annuityAmount);
    setLumpsumWithdrawn(lumpsumAmount);
    setPensionPerMonth(pensionMonthly);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 });
  };

  return (
    <div className="container my-5">
      <h2 className="mb-4">NPS Calculator</h2>

      <div className="row">
        <div className="col-md-6">

          <div className="form-group mb-3">
            <label>Monthly Investment</label>
            <input type="number" className="form-control" value={monthlyInvestment} onChange={(e) => setMonthlyInvestment(+e.target.value)} />
            <input type="range" min="500" max="100000" step="100" value={monthlyInvestment} onChange={(e) => setMonthlyInvestment(+e.target.value)} className="form-range" />
          </div>

          <div className="form-group mb-3">
            <label>Expected Return On Investment (%)</label>
            <input type="number" className="form-control" value={expectedReturn} onChange={(e) => setExpectedReturn(+e.target.value)} />
            <input type="range" min="1" max="20" value={expectedReturn} onChange={(e) => setExpectedReturn(+e.target.value)} className="form-range" />
          </div>

          <div className="form-group mb-3">
            <label>My Current Age</label>
            <input type="number" className="form-control" value={currentAge} onChange={(e) => setCurrentAge(+e.target.value)} />
          </div>

          <div className="form-group mb-3">
            <label>My Retirement Age</label>
            <input type="number" className="form-control" value={retirementAge} onChange={(e) => setRetirementAge(+e.target.value)} />
          </div>

          <div className="form-group mb-3">
            <label>% Of Pension Wealth Invested In Annuity Plan</label>
            <input type="number" className="form-control" value={percentAnnuity} onChange={(e) => setPercentAnnuity(+e.target.value)} />
            <input type="range" min="0" max="100" value={percentAnnuity} onChange={(e) => setPercentAnnuity(+e.target.value)} className="form-range" />
          </div>

          <div className="form-group mb-3">
            <label>Expected Rate of Interest on the Annuity Investment (%)</label>
            <input type="number" className="form-control" value={annuityReturn} onChange={(e) => setAnnuityReturn(+e.target.value)} />
            <input type="range" min="0" max="12" value={annuityReturn} onChange={(e) => setAnnuityReturn(+e.target.value)} className="form-range" />
          </div>

          <div className="form-group mb-3">
            <label>Annuity Period (Years)</label>
            <input type="number" className="form-control" value={annuityPeriod} onChange={(e) => setAnnuityPeriod(+e.target.value)} />
          </div>

        </div>

        <div className="col-md-6">
          <div className="p-4 bg-light rounded">
            <h5>Total Investing Period: {retirementAge - currentAge} Years</h5>
            <p>Invested By You: <strong>{formatCurrency(investedAmount)}</strong></p>
            <p>Interest Earned: <strong>{formatCurrency(interestEarned)}</strong></p>
            <p>Total Tax Saving: <strong>{formatCurrency(taxSaving)}</strong></p>
            <h5 className="text-primary">Pension Wealth Generated: {formatCurrency(pensionWealth)}</h5>

            <hr />

            <p>Pension Amount Reinvested: <strong>{formatCurrency(reinvestedAmount)}</strong></p>
            <p>Lumpsum Amount Withdrawn: <strong>{formatCurrency(lumpsumWithdrawn)}</strong></p>
            <h5 className="text-primary">Pension Per Month Post Retirement: {formatCurrency(pensionPerMonth)}</h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSCalculator;
